import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  TextInput,
  PasswordInput,
  Button,
  Container,
  Anchor,
  Alert,
  Checkbox,
  Title,
  Group
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle } from '@tabler/icons-react';
import styled from '@emotion/styled';
import { useAuthStore } from '../../../application/store/authStore';
import { LoginCommand } from '../../../application/dto/auth/LoginCommand';
import { PUBLIC_ROUTES, ADMIN_ROUTES, CUSTOMER_ROUTES, NONPROFIT_ROUTES } from '../../routing/routes';
import { PublicHeader } from '../../layouts/PublicLayout/PublicHeader';
import { PublicFooter } from '../../layouts/PublicLayout/PublicFooter';

/**
 * LoginPage - User authentication page
 *
 * Sprint 041 Session 4: Restyled to match Marketing site's approved login design.
 * Uses PublicHeader/PublicFooter, narrow card on grey background, "Email" label,
 * disabled button state, and brand color.
 *
 * Features:
 * - Mantine form with email/password validation
 * - Integration with authStore (Zustand)
 * - Role-based redirect after login
 * - Error display
 * - Remember me checkbox
 * - Bot detection (honeypot + timestamp)
 * - Link to registration page
 */
export const LoginPage: React.FC = (): React.ReactElement => {
  const navigate = useNavigate();
  const { login, isLoading, error, clearError } = useAuthStore();

  // SESSION_215: Capture form load timestamp for bot detection
  const [formLoadTime] = useState<number>(() => Date.now());

  // Mantine form with validation
  const form = useForm<LoginCommand>({
    initialValues: {
      userName: '',
      password: '',
      rememberMe: false,
      honeypot: '' // SESSION_213: Bot detection - hidden field
    },

    validate: {
      userName: (value: string): string | null => {
        if (!value || value.trim().length === 0) {
          return 'Email is required';
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          return 'Please enter a valid email address';
        }
        return null;
      },
      password: (value: string): string | null => {
        if (!value || value.trim().length === 0) {
          return 'Password is required';
        }
        if (value.length < 8) {
          return 'Password must be at least 8 characters';
        }
        return null;
      }
    }
  });

  const isFormValid = form.isValid();

  /**
   * Handle form submission
   */
  const handleSubmit = async (values: LoginCommand): Promise<void> => {
    // Clear any previous errors
    clearError();

    try {
      // SESSION_215: Include form load timestamp for bot detection
      const commandWithTimestamp: LoginCommand = {
        ...values,
        formLoadTimestamp: formLoadTime
      };

      // Call login from authStore
      await login(commandWithTimestamp);

      // Redirect based on user role
      const currentUser = useAuthStore.getState().user;

      if (currentUser?.role === 'Admin') {
        navigate(ADMIN_ROUTES.DASHBOARD);
      } else if (currentUser?.role === 'NonProfitUser') {
        navigate(NONPROFIT_ROUTES.DASHBOARD);
      } else {
        navigate(CUSTOMER_ROUTES.DASHBOARD);
      }
    } catch (err) {
      // Error is handled by authStore and set in error state
    }
  };

  return (
    <>
      <PublicHeader />
      <LoginWrapper>
        <Container size="xs">
          <LoginCard>
            <Title order={1} size="h2" mb="xs" ta="center">
              Welcome Back
            </Title>
            <Subtitle>Sign in to your MentionedMe account</Subtitle>

            {/* Error Alert */}
            {error && (
              <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light" mb="md">
                {error}
              </Alert>
            )}

            {/* Login Form */}
            <form onSubmit={form.onSubmit(handleSubmit)}>
              <FormStack>
                <TextInput
                  label="Email"
                  type="email"
                  placeholder="your@email.com"
                  required
                  size="md"
                  {...form.getInputProps('userName')}
                />

                <PasswordInput
                  label="Password"
                  placeholder="Your password"
                  required
                  size="md"
                  {...form.getInputProps('password')}
                />

                {/* SESSION_213: Honeypot field - hidden from users, bots will auto-fill */}
                <TextInput
                  name="website_url"
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    left: '-9999px',
                    opacity: 0,
                    height: 0,
                    width: 0,
                    overflow: 'hidden'
                  }}
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.values.honeypot || ''}
                  onChange={(e): void => form.setFieldValue('honeypot', e.target.value)}
                />

                {/* Remember Me and Forgot Password */}
                <Group justify="space-between">
                  <Checkbox
                    label="Remember me"
                    {...form.getInputProps('rememberMe', { type: 'checkbox' })}
                  />
                  <Anchor component={Link} to={PUBLIC_ROUTES.FORGOT_PASSWORD} size="sm">
                    Forgot password?
                  </Anchor>
                </Group>

                {/* Submit Button */}
                <Button
                  type="submit"
                  size="lg"
                  variant="filled"
                  color="brand"
                  disabled={!isFormValid || isLoading}
                  loading={isLoading}
                  fullWidth
                >
                  {isLoading ? 'Signing in...' : 'Sign In'}
                </Button>
              </FormStack>
            </form>

            <RegisterPrompt>
              Don&apos;t have an account?{' '}
              <Anchor component={Link} to={PUBLIC_ROUTES.REGISTER} fw={600}>
                Create account
              </Anchor>
            </RegisterPrompt>
          </LoginCard>
        </Container>
      </LoginWrapper>
      <PublicFooter />
    </>
  );
};

const LoginWrapper = styled.section`
  padding: 80px 0;
  background-color: #f8f9fa;
  min-height: 70vh;
  display: flex;
  align-items: center;

  @media (max-width: 767px) {
    padding: 48px 0;
  }
`;

const LoginCard = styled.div`
  background-color: #ffffff;
  padding: 48px;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  @media (max-width: 767px) {
    padding: 32px 24px;
  }
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #6c757d;
  text-align: center;
  margin-bottom: 32px;

  @media (max-width: 767px) {
    font-size: 14px;
    margin-bottom: 24px;
  }
`;

const FormStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const RegisterPrompt = styled.p`
  text-align: center;
  margin-top: 24px;
  font-size: 14px;
  color: #6c757d;
`;
